import { useEffect, useMemo, useRef, useState } from "react";
import { Cabine } from "../componentes/FerramentaUi";
import {
  Bloco,
  CAMPO_CLARO,
  CAMPO_NAVY,
  CampoMoeda,
  CampoPercentual,
  GovForaDoAr,
  LinhaDeTributo,
  NumeroHeroi,
  RecusaDoGov,
  Rotulo,
  SeloSimulado,
} from "../componentes/CalculadorasUi";
import { chamar, type Resposta } from "../lib/calculadoras";
import {
  emReais,
  exigeAliquotasNominais,
  faltaNoRegimeGeral,
  FONTE_DA_SUGESTAO,
  lerRegimeGeral,
  montarRoc,
  SUGESTAO_2027,
  type FormularioRegimeGeral,
  type ResultadoRegimeGeral,
} from "../lib/montarCalculo";
import { BASE } from "./Calculadoras";
import { Link } from "react-router-dom";
import { rolarAte } from "../lib/rolarPara";

// A calculadora de Regime Geral — a que o gov chama de "Regime Regular e
// Simples Nacional".
//
// ---------------------------------------------------------------------------
// A ordem dos campos é a ordem em que a resposta do gov depende deles
// ---------------------------------------------------------------------------
// A DATA vem primeiro porque ela decide quase tudo: a tabela de situações
// tributárias, a vigência da classificação, se o NCM está sujeito ao Imposto
// Seletivo naquele dia, e se as alíquotas nominais são obrigatórias. Trocar a
// data depois de preencher o resto invalidaria o que já está na tela — então a
// tela recarrega as listas e avisa, em vez de deixar um CST de outra vigência
// parado num campo.
//
// ---------------------------------------------------------------------------
// O que é digitado e o que é escolhido
// ---------------------------------------------------------------------------
// NCM é DIGITADO e conferido: o gov não tem listagem de NCM (são milhares e ele
// só responde por código exato), então o campo confirma o que o aluno digitou —
// capítulo, posição, subitem, e se aquele código tem Imposto Seletivo. A NBS é
// ESCOLHIDA: ela tem lista (920 itens em 99 KB, medido), e cabe numa ida.
//
// CST e classificação tributária saem de UMA consulta só: a resposta do gov já
// traz as classificações dentro de cada CST. O Bubble gastava duas chamadas
// nisso.

type Estado =
  | { fase: "vazio" }
  | { fase: "calculando" }
  | { fase: "pronto"; r: ResultadoRegimeGeral; simulado: number | null; aviso: string | null; guardado: boolean; motivoDoRegistro: string | null }
  | { fase: "recusa"; titulo: string; detalhe: string }
  | { fase: "indisponivel"; motivo: string };

type Uf = { sigla: string; nome: string; codigo: number };
type Municipio = { codigo: number; nome: string };
type GrupoDfe = { id: string; titulo: string; tipos: { codigo: string; titulo: string; modelo: string }[] };
type Cst = {
  codigo: string;
  descricao: string;
  classificacoesTributarias: { codigo: string; descricao: string }[];
};
type Ncm = {
  tributadoPeloImpostoSeletivo?: boolean;
  aliquotaAdValorem?: number;
  capitulo?: string;
  posicao?: string;
  subitem?: string;
};
type ItemNbs = { codigo: string; descricao: string };

const HOJE = new Date().toISOString().slice(0, 10);

export default function CalcRegimeGeral() {
  const [f, setF] = useState<FormularioRegimeGeral>(() => ({
    dia: HOJE,
    uf: "",
    municipio: null,
    tpDoc: 55,
    compraGov: null,
    item: {
      natureza: "ncm",
      codigo: "",
      cst: "",
      cClassTrib: "",
      baseCalculo: null,
      quantidade: null,
      unidade: "",
      is: null,
      aliquotas: null,
    },
  }));

  const [estado, setEstado] = useState<Estado>({ fase: "vazio" });
  const [rotulo, setRotulo] = useState("");
  const resultadoRef = useRef<HTMLDivElement>(null);

  // As listas de apoio. Todas vêm da mesma function, e nenhuma delas é dado de
  // aluno: são tabelas de referência do gov, guardáveis pelo navegador.
  const [ufs, setUfs] = useState<Uf[] | null>(null);
  const [municipios, setMunicipios] = useState<Municipio[] | null>(null);
  const [dfe, setDfe] = useState<GrupoDfe[] | null>(null);
  const [csts, setCsts] = useState<Cst[] | null>(null);
  const [cstsIs, setCstsIs] = useState<Cst[] | null>(null);
  const [nbs, setNbs] = useState<ItemNbs[] | null>(null);
  const [ncm, setNcm] = useState<Ncm | null>(null);
  const [ncmErro, setNcmErro] = useState<string | null>(null);
  const [conferindoNcm, setConferindoNcm] = useState(false);

  useEffect(() => {
    void chamar<Uf[]>("ufs").then((r) => r.situacao === "ok" && setUfs(r.dados));
    void chamar<GrupoDfe[]>("dfe").then((r) => r.situacao === "ok" && setDfe(r.dados));
  }, []);

  useEffect(() => {
    if (!f.uf) {
      setMunicipios(null);
      return;
    }
    let vivo = true;
    void chamar<Municipio[]>("municipios", { params: { siglaUf: f.uf } }).then((r) => {
      if (vivo && r.situacao === "ok") setMunicipios(r.dados);
    });
    return () => {
      vivo = false;
    };
  }, [f.uf]);

  // As tabelas tributárias mudam com a DATA. Recarregá-las quando ela muda é o
  // que impede um CST de outra vigência ficar parado no campo — o gov recusaria
  // com 404 e o aluno leria "classificação não encontrada" sem saber por quê.
  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.dia)) return;
    let vivo = true;
    void chamar<Cst[]>("csts", { params: { data: f.dia } }).then((r) => {
      if (vivo && r.situacao === "ok") setCsts(r.dados);
    });
    void chamar<Cst[]>("csts-is", { params: { data: f.dia } }).then((r) => {
      if (vivo && r.situacao === "ok") setCstsIs(r.dados);
    });
    void chamar<ItemNbs[]>("nbs-lista", { params: { data: f.dia } }).then((r) => {
      if (vivo && r.situacao === "ok") setNbs(r.dados);
    });
    return () => {
      vivo = false;
    };
  }, [f.dia]);

  const classificacoes = useMemo(
    () => csts?.find((c) => c.codigo === f.item.cst)?.classificacoesTributarias ?? [],
    [csts, f.item.cst],
  );
  const classificacoesIs = useMemo(
    () => cstsIs?.find((c) => c.codigo === f.item.is?.cst)?.classificacoesTributarias ?? [],
    [cstsIs, f.item.is?.cst],
  );

  const precisaNominais = exigeAliquotasNominais(f.dia);
  const falta = faltaNoRegimeGeral(f);

  function mudar(parte: Partial<FormularioRegimeGeral>) {
    setF((v) => ({ ...v, ...parte }));
  }
  function mudarItem(parte: Partial<FormularioRegimeGeral["item"]>) {
    setF((v) => ({ ...v, item: { ...v.item, ...parte } }));
  }

  /**
   * Confere o NCM no gov e diz o que ele é.
   *
   * O campo mais importante desta resposta é `tributadoPeloImpostoSeletivo`: é
   * ele que faz o grupo do IS aparecer. Sem essa conferência, quem digitasse um
   * NCM de bebida receberia um 422 seco ("Dados do Imposto Seletivo devem ser
   * informados para NCM 22021000") depois de preencher a tela inteira.
   */
  async function conferirNcm() {
    const codigo = f.item.codigo.replace(/\D/g, "");
    if (codigo.length < 8 || !/^\d{4}-\d{2}-\d{2}$/.test(f.dia)) return;
    setConferindoNcm(true);
    setNcmErro(null);
    const r = await chamar<Ncm>("ncm", { params: { ncm: codigo, data: f.dia } });
    setConferindoNcm(false);
    if (r.situacao === "ok") {
      setNcm(r.dados);
      // Ligar o grupo do IS é sugestão, não imposição: o aluno pode desligar (há
      // classificações do IS que dispensam o cálculo). O que não pode é a tela
      // esconder que aquele NCM tem IS.
      if (r.dados?.tributadoPeloImpostoSeletivo && !f.item.is) {
        mudarItem({
          is: {
            cst: "",
            cClassTrib: "",
            baseCalculo: f.item.baseCalculo,
            adValorem: r.dados.aliquotaAdValorem ?? null,
          },
        });
      }
      return;
    }
    setNcm(null);
    setNcmErro(
      r.situacao === "recusa"
        ? r.detalhe
        : "Não conseguimos conferir este NCM agora. Você pode calcular assim mesmo.",
    );
  }

  async function calcular() {
    if (falta.length > 0) return;
    setEstado({ fase: "calculando" });
    const corpo = montarRoc(f);
    const r: Resposta<unknown> = await chamar("regime-geral", {
      corpo,
      guardar: { calculadora: "regime-geral", rotulo: rotulo.trim() || null },
    });

    if (r.situacao === "ok") {
      setEstado({
        fase: "pronto",
        r: lerRegimeGeral(r.dados),
        simulado: r.simulado,
        aviso: r.aviso,
        guardado: r.guardado,
        motivoDoRegistro: r.motivoDoRegistro,
      });
    } else if (r.situacao === "recusa") {
      setEstado({ fase: "recusa", titulo: r.titulo, detalhe: r.detalhe });
    } else {
      setEstado({ fase: "indisponivel", motivo: "motivo" in r ? r.motivo : "falha" });
    }
    // Levar o olho até o resultado faz parte do clique: sem isto, quem calcula
    // com a tela rolada no meio do formulário não vê nada mudar e conclui que o
    // botão não pegou. A peça não rola quando o alvo já está inteiro na tela.
    requestAnimationFrame(() => rolarAte(resultadoRef.current));
  }

  return (
    <>
      <Cabine
        trilha={{ para: `${BASE}${window.location.search}`, rotulo: "Calculadoras" }}
        rotulo="Calculadora"
        titulo="Regime Geral"
        descricao="Informe a operação e a Receita devolve o IBS, a CBS e o Imposto Seletivo, com a memória de cálculo de cada um."
        rodape={
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Rotulo navy para="rg-data">
                Data do fato gerador
              </Rotulo>
              <input
                id="rg-data"
                type="date"
                className={CAMPO_NAVY}
                value={f.dia}
                onChange={(e) => mudar({ dia: e.target.value })}
              />
            </div>
            <div>
              <Rotulo navy para="rg-uf">
                UF
              </Rotulo>
              <select
                id="rg-uf"
                className={CAMPO_NAVY}
                value={f.uf}
                onChange={(e) => mudar({ uf: e.target.value, municipio: null })}
              >
                {/* A cor do MENU suspenso é do TERRITÓRIO (`.painel-navy option`
                    no index.css), não desta chamada: o popup é desenhado fora da
                    página e duas tentativas já falharam aqui — `text-tinta` em
                    cada opção, que é token semântico dentro do navy, e
                    `color-scheme` solto, que Victor viu falhar na tela. */}
                <option value="">Escolha…</option>
                {(ufs ?? []).map((u) => (
                  <option key={u.sigla} value={u.sigla}>
                    {u.sigla} — {u.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Rotulo navy para="rg-municipio">
                Município
              </Rotulo>
              <select
                id="rg-municipio"
                className={CAMPO_NAVY}
                value={f.municipio ?? ""}
                disabled={!f.uf}
                onChange={(e) => mudar({ municipio: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">{f.uf ? "Escolha…" : "Escolha a UF antes"}</option>
                {(municipios ?? []).map((m) => (
                  <option key={m.codigo} value={m.codigo}>
                    {m.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        }
      />

      <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_22rem]">
        <div className="flex flex-col gap-5">
          <Bloco
            titulo="A operação"
            descricao="O documento fiscal e o que está sendo vendido. O tipo de documento decide a tabela de situações tributárias que a Receita aplica."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Rotulo para="rg-tpdoc">Tipo de documento fiscal</Rotulo>
                <select
                  id="rg-tpdoc"
                  className={CAMPO_CLARO}
                  value={f.tpDoc ?? ""}
                  onChange={(e) => mudar({ tpDoc: e.target.value ? Number(e.target.value) : null })}
                >
                  <option value="">Escolha…</option>
                  {(dfe ?? []).map((g) => (
                    <optgroup key={g.id} label={g.titulo}>
                      {g.tipos.map((t) => (
                        <option key={t.codigo} value={Number(t.modelo)}>
                          {t.titulo} ({t.modelo})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div>
                <Rotulo para="rg-natureza">O que está sendo fornecido</Rotulo>
                <select
                  id="rg-natureza"
                  className={CAMPO_CLARO}
                  value={f.item.natureza}
                  onChange={(e) => {
                    // Trocar a natureza limpa o código: um NCM no campo de NBS
                    // é recusado pelo gov com uma mensagem que não explica que o
                    // problema é o campo errado.
                    setNcm(null);
                    setNcmErro(null);
                    mudarItem({ natureza: e.target.value as "ncm" | "nbs", codigo: "", is: null });
                  }}
                >
                  <option value="ncm">Mercadoria (NCM)</option>
                  <option value="nbs">Serviço (NBS)</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              {f.item.natureza === "ncm" ? (
                <>
                  <Rotulo para="rg-ncm">Código NCM</Rotulo>
                  <div className="flex flex-wrap gap-2">
                    <input
                      id="rg-ncm"
                      className={`${CAMPO_CLARO} flex-1 tabular-nums`}
                      inputMode="numeric"
                      placeholder="6109.10.00"
                      value={f.item.codigo}
                      onChange={(e) => {
                        setNcm(null);
                        setNcmErro(null);
                        mudarItem({ codigo: e.target.value });
                      }}
                      onBlur={() => void conferirNcm()}
                    />
                    <button
                      type="button"
                      className="rounded-xl border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-laranja disabled:opacity-50"
                      disabled={conferindoNcm}
                      onClick={() => void conferirNcm()}
                    >
                      {conferindoNcm ? "Conferindo…" : "Conferir"}
                    </button>
                  </div>
                  {ncm && (
                    <div className="mt-2 rounded-xl bg-superficie-2 p-3">
                      <p className="text-xs leading-relaxed text-cinza">
                        <strong className="text-tinta">{ncm.capitulo}</strong>
                        {ncm.posicao && ` · ${ncm.posicao}`}
                        {ncm.subitem && ` · ${ncm.subitem}`}
                      </p>
                      {ncm.tributadoPeloImpostoSeletivo && (
                        <p className="mt-1.5 text-xs leading-relaxed font-semibold text-alerta-tinta">
                          Este NCM é tributado pelo Imposto Seletivo
                          {ncm.aliquotaAdValorem !== undefined &&
                            ` (alíquota ad valorem de ${String(ncm.aliquotaAdValorem).replace(".", ",")}%)`}
                          . Preencha o bloco do Imposto Seletivo abaixo — sem ele a Receita
                          recusa o cálculo.
                        </p>
                      )}
                    </div>
                  )}
                  {ncmErro && (
                    <p className="mt-2 text-xs leading-relaxed text-erro-tinta">{ncmErro}</p>
                  )}
                </>
              ) : (
                <>
                  <Rotulo para="rg-nbs">Código NBS</Rotulo>
                  <select
                    id="rg-nbs"
                    className={CAMPO_CLARO}
                    value={f.item.codigo}
                    onChange={(e) => mudarItem({ codigo: e.target.value })}
                  >
                    <option value="">
                      {nbs ? "Escolha o serviço…" : "Carregando a lista de serviços…"}
                    </option>
                    {(nbs ?? []).map((n) => (
                      <option key={n.codigo} value={n.codigo}>
                        {n.codigo} — {n.descricao}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Rotulo para="rg-cst">Situação tributária (CST)</Rotulo>
                <select
                  id="rg-cst"
                  className={CAMPO_CLARO}
                  value={f.item.cst}
                  onChange={(e) => mudarItem({ cst: e.target.value, cClassTrib: "" })}
                >
                  <option value="">{csts ? "Escolha…" : "Carregando…"}</option>
                  {(csts ?? []).map((c) => (
                    <option key={c.codigo} value={c.codigo}>
                      {c.codigo} — {c.descricao}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Rotulo para="rg-classtrib">Classificação tributária</Rotulo>
                <select
                  id="rg-classtrib"
                  className={CAMPO_CLARO}
                  value={f.item.cClassTrib}
                  disabled={!f.item.cst}
                  onChange={(e) => mudarItem({ cClassTrib: e.target.value })}
                >
                  <option value="">{f.item.cst ? "Escolha…" : "Escolha a CST antes"}</option>
                  {classificacoes.map((c) => (
                    <option key={c.codigo} value={c.codigo}>
                      {c.codigo} — {c.descricao}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div>
                <Rotulo para="rg-base">Base de cálculo</Rotulo>
                <CampoMoeda
                  id="rg-base"
                  valor={f.item.baseCalculo}
                  aoMudar={(n) => mudarItem({ baseCalculo: n })}
                />
              </div>
              <div>
                <Rotulo para="rg-qtd">Quantidade (opcional)</Rotulo>
                <input
                  id="rg-qtd"
                  className={`${CAMPO_CLARO} tabular-nums`}
                  inputMode="decimal"
                  placeholder="1"
                  value={f.item.quantidade ?? ""}
                  onChange={(e) => {
                    const n = Number(e.target.value.replace(",", "."));
                    mudarItem({
                      quantidade: e.target.value.trim() === "" || !Number.isFinite(n) ? null : n,
                    });
                  }}
                />
              </div>
              <div>
                <Rotulo para="rg-unidade">Unidade (opcional)</Rotulo>
                <input
                  id="rg-unidade"
                  className={CAMPO_CLARO}
                  placeholder="UN, LT, KG…"
                  maxLength={6}
                  value={f.item.unidade}
                  onChange={(e) => mudarItem({ unidade: e.target.value })}
                />
              </div>
            </div>
          </Bloco>

          {f.item.is !== null && (
            <Bloco
              titulo="Imposto Seletivo"
              descricao="O IS é calculado primeiro e ENTRA na base do IBS e da CBS — é por isso que a base do resultado fica maior que o valor da mercadoria."
              acao={
                <button
                  type="button"
                  className="text-xs font-semibold text-link hover:underline"
                  onClick={() => mudarItem({ is: null })}
                >
                  Não calcular IS
                </button>
              }
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Rotulo para="rg-is-cst">CST do Imposto Seletivo</Rotulo>
                  <select
                    id="rg-is-cst"
                    className={CAMPO_CLARO}
                    value={f.item.is.cst}
                    onChange={(e) =>
                      mudarItem({ is: { ...f.item.is!, cst: e.target.value, cClassTrib: "" } })
                    }
                  >
                    <option value="">{cstsIs ? "Escolha…" : "Carregando…"}</option>
                    {(cstsIs ?? []).map((c) => (
                      <option key={c.codigo} value={c.codigo}>
                        {c.codigo} — {c.descricao}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Rotulo para="rg-is-classtrib">Classificação tributária do IS</Rotulo>
                  <select
                    id="rg-is-classtrib"
                    className={CAMPO_CLARO}
                    value={f.item.is.cClassTrib}
                    disabled={!f.item.is.cst}
                    onChange={(e) => mudarItem({ is: { ...f.item.is!, cClassTrib: e.target.value } })}
                  >
                    <option value="">{f.item.is.cst ? "Escolha…" : "Escolha a CST antes"}</option>
                    {classificacoesIs.map((c) => (
                      <option key={c.codigo} value={c.codigo}>
                        {c.codigo} — {c.descricao}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Rotulo para="rg-is-base">Base de cálculo do IS</Rotulo>
                  <CampoMoeda
                    id="rg-is-base"
                    valor={f.item.is.baseCalculo}
                    aoMudar={(n) => mudarItem({ is: { ...f.item.is!, baseCalculo: n } })}
                  />
                </div>
                {precisaNominais && (
                  <div>
                    <Rotulo para="rg-is-aliq">Alíquota ad valorem do IS</Rotulo>
                    <CampoPercentual
                      id="rg-is-aliq"
                      valor={f.item.is.adValorem}
                      aoMudar={(n) => mudarItem({ is: { ...f.item.is!, adValorem: n } })}
                    />
                  </div>
                )}
              </div>
            </Bloco>
          )}

          {precisaNominais && (
            <Bloco
              titulo="Alíquotas nominais"
              descricao={
                <>
                  A partir de 2027 a Receita <strong className="text-tinta">exige</strong> as três
                  alíquotas — e não as fornece: as de referência ainda não estão em lei.{" "}
                  {FONTE_DA_SUGESTAO}
                </>
              }
              acao={
                <button
                  type="button"
                  className="text-xs font-semibold text-link hover:underline"
                  onClick={() => mudarItem({ aliquotas: { ...SUGESTAO_2027 } })}
                >
                  Usar a estimativa
                </button>
              }
            >
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Rotulo para="rg-a-cbs">CBS</Rotulo>
                  <CampoPercentual
                    id="rg-a-cbs"
                    valor={f.item.aliquotas?.cbs ?? null}
                    aoMudar={(n) =>
                      mudarItem({
                        aliquotas: {
                          cbs: n,
                          ibsEstadual: f.item.aliquotas?.ibsEstadual ?? null,
                          ibsMunicipal: f.item.aliquotas?.ibsMunicipal ?? null,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <Rotulo para="rg-a-ibsuf">IBS estadual</Rotulo>
                  <CampoPercentual
                    id="rg-a-ibsuf"
                    valor={f.item.aliquotas?.ibsEstadual ?? null}
                    aoMudar={(n) =>
                      mudarItem({
                        aliquotas: {
                          cbs: f.item.aliquotas?.cbs ?? null,
                          ibsEstadual: n,
                          ibsMunicipal: f.item.aliquotas?.ibsMunicipal ?? null,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <Rotulo para="rg-a-ibsmun">IBS municipal</Rotulo>
                  <CampoPercentual
                    id="rg-a-ibsmun"
                    valor={f.item.aliquotas?.ibsMunicipal ?? null}
                    aoMudar={(n) =>
                      mudarItem({
                        aliquotas: {
                          cbs: f.item.aliquotas?.cbs ?? null,
                          ibsEstadual: f.item.aliquotas?.ibsEstadual ?? null,
                          ibsMunicipal: n,
                        },
                      })
                    }
                  />
                </div>
              </div>
            </Bloco>
          )}

          <Bloco
            titulo="Compra governamental"
            descricao="Quando o comprador é ente público, a LC 214/2025 prevê redução de alíquota. O percentual do redutor ainda não foi fixado em lei — o piloto responde 0%."
            acao={
              <label className="flex items-center gap-2 text-xs font-semibold text-tinta">
                <input
                  type="checkbox"
                  className="size-4 accent-acento"
                  checked={f.compraGov !== null}
                  onChange={(e) =>
                    mudar({ compraGov: e.target.checked ? { tpEnteGov: 1, tpOperGov: null } : null })
                  }
                />
                É compra governamental
              </label>
            }
          >
            {f.compraGov === null ? (
              <p className="text-sm text-cinza">
                Marque acima quando a operação for com ente público.
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Rotulo para="rg-ente">Tipo de ente</Rotulo>
                  <select
                    id="rg-ente"
                    className={CAMPO_CLARO}
                    value={f.compraGov.tpEnteGov}
                    onChange={(e) =>
                      mudar({ compraGov: { ...f.compraGov!, tpEnteGov: Number(e.target.value) } })
                    }
                  >
                    <option value={1}>1 — União</option>
                    <option value={2}>2 — Estado</option>
                    <option value={3}>3 — Distrito Federal</option>
                    <option value={4}>4 — Município</option>
                  </select>
                </div>
                <div>
                  <Rotulo para="rg-oper">Tipo de operação (opcional)</Rotulo>
                  <select
                    id="rg-oper"
                    className={CAMPO_CLARO}
                    value={f.compraGov.tpOperGov ?? ""}
                    onChange={(e) =>
                      mudar({
                        compraGov: {
                          ...f.compraGov!,
                          tpOperGov: e.target.value ? Number(e.target.value) : null,
                        },
                      })
                    }
                  >
                    <option value="">Não informar</option>
                    <option value={1}>1 — Fornecimento com pagamento pelo ente</option>
                    <option value={2}>2 — Demais casos</option>
                  </select>
                </div>
              </div>
            )}
          </Bloco>
        </div>

        {/* A coluna do resultado GRUDA no desktop e desce no celular: o aluno
            mexe num campo e vê o número sem rolar. É a mesma decisão do painel
            da BancadaDeCalculo, e aqui ela vale ainda mais porque o formulário é
            longo. */}
        <div className="lg:sticky lg:top-24 lg:self-start" ref={resultadoRef}>
          <div className="rounded-cf-lg border border-borda bg-superficie p-5">
            <h2 className="font-display text-base font-bold text-titulo">Resultado</h2>

            <div className="mt-3">
              <Rotulo para="rg-rotulo">Nome deste cálculo (opcional)</Rotulo>
              <input
                id="rg-rotulo"
                className={CAMPO_CLARO}
                placeholder="Venda para o cliente X"
                maxLength={120}
                value={rotulo}
                onChange={(e) => setRotulo(e.target.value)}
              />
            </div>

            <button
              type="button"
              className="pill-cta mt-4 w-full disabled:opacity-50"
              disabled={falta.length > 0 || estado.fase === "calculando"}
              onClick={() => void calcular()}
            >
              {estado.fase === "calculando" ? "Calculando…" : "Calcular na Receita"}
            </button>

            {falta.length > 0 && (
              // A lista do que falta fica VISÍVEL, e não escondida atrás de um
              // botão desabilitado sem explicação. Botão apagado sem motivo é o
              // defeito que a `pill-desligada` nasceu para resolver na
              // Secretaria: o aluno olha para ele tentando entender.
              <p className="mt-2 text-xs leading-relaxed text-cinza">
                Falta preencher: {falta.join(", ")}.
              </p>
            )}

            <div className="mt-4 flex flex-col gap-4">
              {estado.fase === "recusa" && (
                <RecusaDoGov titulo={estado.titulo} detalhe={estado.detalhe} />
              )}
              {estado.fase === "indisponivel" && <GovForaDoAr motivo={estado.motivo} />}
              {estado.fase === "pronto" && (
                <>
                  <SeloSimulado codigo={estado.simulado} texto={estado.aviso} />
                  <NumeroHeroi
                    rotulo="Total de tributos"
                    valor={emReais(estado.r.totalTributos)}
                    apoio={
                      <>
                        Sobre a base de {emReais(estado.r.baseCalculo)}
                        {estado.r.impostoSeletivo &&
                          " — que já inclui o Imposto Seletivo, como manda a LC 214/2025"}
                        .
                      </>
                    }
                  />
                  <div>
                    {estado.r.impostoSeletivo && (
                      <LinhaDeTributo
                        rotulo={estado.r.impostoSeletivo.rotulo}
                        aliquota={estado.r.impostoSeletivo.aliquota}
                        valor={estado.r.impostoSeletivo.valor}
                        memoria={estado.r.impostoSeletivo.memoria}
                      />
                    )}
                    {estado.r.linhas.map((l) => (
                      <LinhaDeTributo
                        key={l.rotulo}
                        rotulo={l.rotulo}
                        aliquota={l.aliquota}
                        valor={l.valor}
                        memoria={l.memoria}
                      />
                    ))}
                  </div>
                  {estado.r.redutorCompraGov !== null && (
                    <p className="text-xs leading-relaxed text-cinza">
                      Redutor de compra governamental aplicado:{" "}
                      <strong className="text-tinta tabular-nums">
                        {String(estado.r.redutorCompraGov).replace(".", ",")}%
                      </strong>
                      .
                    </p>
                  )}
                  {!estado.guardado && (
                    <p className="text-xs leading-relaxed text-cinza">
                      {estado.motivoDoRegistro === "historico-cheio"
                        ? "O seu histórico está cheio (500 cálculos). Este resultado não foi guardado — apague alguns na página inicial da ferramenta."
                        : "Este cálculo não foi guardado no seu histórico, mas o resultado acima é válido."}
                    </p>
                  )}
                </>
              )}
              {estado.fase === "vazio" && (
                <p className="text-sm leading-relaxed text-cinza">
                  Preencha a operação ao lado e clique em calcular. A resposta vem da
                  calculadora da Receita Federal, com a memória de cálculo de cada tributo.
                </p>
              )}
            </div>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-cinza">
            <Link to={`${BASE}${window.location.search}`} className="text-link hover:underline">
              Voltar às três calculadoras
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
